import { Dispatch, useEffect, useState } from 'react';

export function useStateQueryString<T extends string = string>(key: string): [T | null, Dispatch<T | null>] {
	const initial = new URLSearchParams(window.location.search);

	const [value, setValue] = useState<T | null>(initial.get(key) as T);

	// sync the search params to the address bar
	useEffect(() => {
		const search = new URLSearchParams(window.location.search);
		if (value) search.set(key, value);
		else search.delete(key);

		const qs = search.toString();
		if (window.location.search !== qs) {
			const updated = window.location.pathname + (qs ? '?' + qs : '');
			window.history.replaceState(null, '', updated);
		}
	}, [key, value]);

	return [value, setValue];
}

/** Enables a simpler query string mapping
 *
 * @example
 * Pass in a map of key to value for type T allowing you to deal with exactly what you want, whereas querystring deals only in strings
 *
 * ```ts
 * const [isAddDrawerOpen, setIsAddDrawerOpen] = useStateQueryStringMap(SUBSCRIPTION_QUERY_KEY, { add: true }, false);
 * ```
 */
export function useStateQueryStringMap<T>(key: string, map: Record<string, T>, defaultValue: T): [T, Dispatch<T | null>] {
	const [value, setValue] = useStateQueryString(key);

	return [value ? map[value] : defaultValue, (v: T | null) => setValue(Object.entries(map).find(f => f[1] === v)?.[0] ?? null)];
}

/**
 * Function to use useStateQueryString hook and treat all values as a Number
 * key - String for picking up from url params. Ex,: 'step' - url - 'www.test.com/page=1321&step=1`
 * defaultValue - Number - default number to set value if it is null
 * @returns - [value, setValue] = state update func, to get a Number original value and set Number as a state update
 */
export function useStateQueryStringToNumber(key: string, defaultValue: number): [number, Dispatch<number | null>] {
	const [value, setValue] = useStateQueryString(key);
	// todo make a context that enables detecting and nesting of this information

	const numberValue = Number(value);

	return [numberValue ?? defaultValue, (v: number | null) => setValue(String(v))];
}
